import type { ReceiptTemplateSettings } from '@/types';

export type ReceiptLayoutMode = 'standard' | 'compact' | 'elegant' | 'short' | 'modern';

export const DEFAULT_RECEIPT_TEMPLATE: ReceiptTemplateSettings = {
  header_text: 'Smart Solutions & Services',
  footer_text: 'We appreciate your trust and look forward to serving you again.',
  show_logo: true,
  logo_width: 120,
  show_served_by: true,
  show_customer: true,
  show_item_code: false,
  font_size: 'medium',
  layout_mode: 'standard',
  show_email: true,
  show_phone: true,
  show_address: true,
  show_item_price: true,
  show_totals: true,
};

export interface ReceiptPreset {
  id: ReceiptLayoutMode;
  name: string;
  description: string;
  bestFor: string;
  /** Defaults applied when selecting this preset card */
  apply: Partial<ReceiptTemplateSettings>;
}

export const RECEIPT_PRESETS: ReceiptPreset[] = [
  {
    id: 'compact',
    name: 'Thermal Compact',
    description: 'Dense layout for 58/80mm thermal printers.',
    bestFor: 'Thermal paper',
    apply: { 
      layout_mode: 'compact', 
      font_size: 'small', 
      show_logo: true,
      show_email: false,
      show_phone: true,
      show_address: false,
      show_item_price: true,
      show_totals: true,
      show_served_by: true,
      show_customer: true,
    },
  },
  {
    id: 'short',
    name: 'Paper Saver',
    description: 'Ultra-short ticket — minimal header, truncated invoice id.',
    bestFor: 'Least paper',
    apply: {
      layout_mode: 'short',
      font_size: 'small',
      show_logo: false,
      show_email: false,
      show_phone: false,
      show_address: false,
      show_item_price: false,
      show_totals: true,
      show_served_by: false,
      show_customer: false,
    },
  },
  {
    id: 'standard',
    name: 'Classic Invoice',
    description: 'Branded A4-style invoice for desktop print and PDF look.',
    bestFor: 'Print / archive',
    apply: { 
      layout_mode: 'standard', 
      font_size: 'medium', 
      show_logo: true,
      show_email: true,
      show_phone: true,
      show_address: true,
      show_item_price: true,
      show_totals: true,
      show_served_by: true,
      show_customer: true,
    },
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Boutique serif look with centered branding.',
    bestFor: 'Premium print',
    apply: { 
      layout_mode: 'elegant', 
      font_size: 'medium', 
      show_logo: true,
      show_email: true,
      show_phone: true,
      show_address: true,
      show_item_price: true,
      show_totals: true,
      show_served_by: true,
      show_customer: true,
    },
  },
  {
    id: 'modern',
    name: 'Modern Digital',
    description: 'Clean card layout optimized for PNG and WhatsApp share.',
    bestFor: 'Image / WhatsApp',
    apply: {
      layout_mode: 'modern',
      font_size: 'medium',
      show_logo: true,
      show_email: false,
      show_phone: true,
      show_address: false,
      show_item_price: true,
      show_totals: true,
      show_served_by: true,
      show_customer: true,
    },
  },
];

export function parseReceiptTemplateSettings(
  raw?: ReceiptTemplateSettings | string | null,
): ReceiptTemplateSettings {
  if (!raw) return { ...DEFAULT_RECEIPT_TEMPLATE };
  if (typeof raw === 'string') {
    try {
      return { ...DEFAULT_RECEIPT_TEMPLATE, ...JSON.parse(raw) };
    } catch {
      return { ...DEFAULT_RECEIPT_TEMPLATE };
    }
  }
  return { ...DEFAULT_RECEIPT_TEMPLATE, ...raw };
}

export function normalizeLayoutMode(mode?: string | null): ReceiptLayoutMode {
  if (mode === 'compact' || mode === 'elegant' || mode === 'short' || mode === 'modern' || mode === 'standard') {
    return mode;
  }
  return 'standard';
}
